import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { getGameByCode, joinTeam } from "@/lib/api";

export default function Landing() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !name.trim()) {
      toast.error("Enter a 6-char game code + team name");
      return;
    }
    setLoading(true);
    try {
      await getGameByCode(code.toUpperCase());
      const team = await joinTeam({ game_code: code.toUpperCase(), name: name.trim() });
      localStorage.setItem(`team_${team.id}`, JSON.stringify(team));
      toast.success(`Joined as ${team.name}`);
      nav(`/team/${team.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join game");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell
      right={
        <Link to="/host/new" data-testid="host-nav-link" className="dxc-btn text-xs">
          Host Console
        </Link>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="font-mono-data text-[10px] tracking-[0.35em] text-[color:var(--dxc-volt)] uppercase">
            Field Exercise / High Stakes
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] mt-4">
            Order the <span className="italic">right</span> test.
            <br />
            Save the <span className="text-[color:var(--dxc-volt)]">budget</span>.
          </h1>
          <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
            A live diagnostic-stewardship game for medical residents and students.
            Each team gets <span className="font-mono-data text-white">$10,000</span> and a
            single patient. Every scan, every panel, every culture — spend wisely.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-0 border border-white/15">
            {[
              { k: "01", t: "Host opens a case" },
              { k: "02", t: "Teams join with a code" },
              { k: "03", t: "Buy tests. Submit dx." },
            ].map((s, i) => (
              <div
                key={s.k}
                className={`p-5 ${i < 2 ? "border-r border-white/15" : ""}`}
              >
                <div className="font-mono-data text-[color:var(--dxc-volt)] text-xs tracking-widest">
                  {s.k}
                </div>
                <div className="mt-2 font-serif-display text-xl leading-snug">{s.t}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <form onSubmit={handleJoin} className="dxc-card p-8" data-testid="join-form">
            <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
              Join / Team Console
            </div>
            <h2 className="font-serif-display text-3xl mt-2">Enter Game Code</h2>

            <div className="mt-6">
              <label className="font-mono-data text-[10px] tracking-widest text-white/60 uppercase">
                Game Code
              </label>
              <input
                data-testid="join-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="ABC123"
                className="dxc-input mt-2 font-mono-data text-3xl tracking-[0.4em] uppercase"
                autoFocus
              />
            </div>

            <div className="mt-5">
              <label className="font-mono-data text-[10px] tracking-widest text-white/60 uppercase">
                Team Name
              </label>
              <input
                data-testid="join-name-input"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 40))}
                placeholder="Team Vitals"
                className="dxc-input mt-2"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="join-submit-button"
              className="dxc-btn dxc-btn-volt w-full mt-6 text-sm"
            >
              {loading ? "Joining…" : "Deploy Team →"}
            </button>

            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Running the session?
              </div>
              <Link
                to="/host/new"
                data-testid="host-cta-link"
                className="dxc-btn block w-full mt-3 text-center text-xs"
              >
                Open Host Console
              </Link>
            </div>
          </form>
        </div>
      </div>
    </Shell>
  );
}
